/**
 * Transactional HUD: baseline on load; last `.anchor-point` within the early-trigger zone (EARLY_TRIGGER_PX) drives overrides.
 * Rules from window.odx.hudRegistry.get(anchorId): null/unknown/disabled → baseline (get omits disabled entries).
 *
 * With `data-odx-nav-invisi-swing` on `<html>`: **non-hero** sections never use the early trigger. Registry swap for the
 * incoming panel happens **only** when invisi-swing calls `applyIncomingHudAtTeleport(section)` at the teleport
 * (nav at section top − 100px, then chases flush; invisible until close). While `odxInvisiSwingHud.teleportSectionId` is set, `runHudTick` sustains HUD
 * from `.anchor-point` inside that section only. Hero (home) keeps the early trigger only.
 */
(function () {
  'use strict';

  var REGISTRY_INLINE_STYLE_ID = 'odx-hud-registry-inline-css';

  var baselineHUD = null;
  var header = null;
  var wrapper = null;
  /** @type {string|null} */
  var appliedHudKey = null;
  var rafScheduled = false;
  var io = null;
  /** Early Trigger (px): HUD activates before anchor reaches viewport top — gives Gravity / transition lead-in */
  var EARLY_TRIGGER_PX = 100;

  function getRegistry() {
    return window.odx && window.odx.hudRegistry ? window.odx.hudRegistry : null;
  }

  function findWrapper() {
    return document.querySelector('sticky-header.odx-header-wrapper, div.header-wrapper.odx-header-wrapper');
  }

  function blurRequiredForStyle(style) {
    var s = (style || 'inherit').toLowerCase();
    return s === 'glass' || s === 'frosted';
  }

  function shouldActivateBlurPodsFromConfig(cfg) {
    if (!cfg || !cfg.enabled) return false;
    if (cfg.blur_enable === true) return true;
    return blurRequiredForStyle(cfg.style);
  }

  /**
   * DOM `data-hud-enabled="false"` forces baseline (even if registry has an entry).
   * `hudRegistry.get` already returns null when registry `enabled` is false / missing.
   *
   * @returns {{ key: string, config: object } | null}
   */
  function resolveHudForAnchor(el) {
    if (!el) return null;
    if (el.dataset && el.dataset.hudEnabled === 'false') return null;

    var reg = getRegistry();
    if (!reg) return null;
    var aid = (el.id || '').trim();
    if (!aid) return null;

    var cfg = reg.get(aid);
    if (!cfg || typeof cfg !== 'object') return null;
    return { key: aid, config: cfg };
  }

  function setRegistryInlineCss(text) {
    var el = document.getElementById(REGISTRY_INLINE_STYLE_ID);
    if (!text) {
      if (el) el.textContent = '';
      return;
    }
    if (!el) {
      el = document.createElement('style');
      el.id = REGISTRY_INLINE_STYLE_ID;
      document.head.appendChild(el);
    }
    el.textContent = text;
  }

  function collectBaseline() {
    header = document.getElementById('site-header');
    wrapper = findWrapper();
    if (!header) return;

    var imgs = header.querySelectorAll('img.header__heading-logo');
    var logo = [];
    imgs.forEach(function (img) {
      logo.push({
        el: img,
        src: img.currentSrc || img.src || '',
        srcset: img.getAttribute('srcset') || '',
      });
    });

    var hcs = window.getComputedStyle(header);
    baselineHUD = {
      logo: logo,
      headerColor: hcs.color,
      headerBoxShadow: hcs.boxShadow,
      headerBackground: hcs.backgroundColor,
    };

    if (wrapper) {
      wrapper.style.setProperty('--odx-hud-blur-px', '0px');
      wrapper.removeAttribute('data-odx-hud-blur-active');
    }
  }

  function revertToBaseline() {
    if (!baselineHUD || !header) return;

    appliedHudKey = null;

    /* Blur pods + .odx-hud--override first so CSS (0.25s) can run; inline paint next frame */
    if (wrapper) {
      wrapper.style.setProperty('--odx-hud-blur-px', '0px');
      wrapper.removeAttribute('data-odx-hud-blur-active');
    }
    header.classList.remove('odx-hud--override');
    setRegistryInlineCss('');

    window.requestAnimationFrame(function () {
      if (!baselineHUD || !header) return;
      header.style.removeProperty('--odx-hud-text-color');
      header.style.color = baselineHUD.headerColor;
      header.style.boxShadow = baselineHUD.headerBoxShadow;
      header.style.backgroundColor = baselineHUD.headerBackground;

      baselineHUD.logo.forEach(function (entry) {
        if (!entry.el) return;
        entry.el.src = entry.src;
        if (entry.srcset) entry.el.setAttribute('srcset', entry.srcset);
        else entry.el.removeAttribute('srcset');
      });
    });
  }

  function applyHudFromConfig(cfg) {
    if (!cfg || !baselineHUD || !header) return;

    /* Re-arm GPU drop on every HUD swap (A→B): reset Y so translate goes 0 → 4px again */
    if (header.classList.contains('odx-hud--override')) {
      header.classList.remove('odx-hud--override');
      void header.offsetWidth;
    }
    header.classList.add('odx-hud--override');

    if (cfg.text_color) {
      header.style.setProperty('--odx-hud-text-color', cfg.text_color);
    } else {
      header.style.removeProperty('--odx-hud-text-color');
    }

    if (cfg.shadow === true) {
      header.style.boxShadow = '0 4px 24px rgba(0, 0, 0, 0.18)';
    } else {
      header.style.boxShadow = 'none';
    }

    var style = (cfg.style || 'inherit').toLowerCase();
    if (style === 'glass') {
      header.style.backgroundColor = 'transparent';
    } else if (style === 'solid') {
      header.style.backgroundColor = 'rgba(18, 18, 18, 0.92)';
    } else if (style === 'transparent') {
      header.style.backgroundColor = 'transparent';
    } else {
      header.style.backgroundColor = baselineHUD.headerBackground;
    }

    var logoUrl = (cfg.logo_url || '').trim();
    if (logoUrl) {
      header.querySelectorAll('img.header__heading-logo').forEach(function (img) {
        img.src = logoUrl;
        img.removeAttribute('srcset');
      });
    } else {
      baselineHUD.logo.forEach(function (entry) {
        if (!entry.el) return;
        entry.el.src = entry.src;
        if (entry.srcset) entry.el.setAttribute('srcset', entry.srcset);
        else entry.el.removeAttribute('srcset');
      });
    }

    if (wrapper) {
      if (shouldActivateBlurPodsFromConfig(cfg)) {
        var px = parseInt(cfg.blur_amount, 10);
        if (isNaN(px) || px < 0) px = 12;
        wrapper.style.setProperty('--odx-hud-blur-px', px + 'px');
        wrapper.setAttribute('data-odx-hud-blur-active', 'true');
      } else {
        wrapper.style.setProperty('--odx-hud-blur-px', '0px');
        wrapper.removeAttribute('data-odx-hud-blur-active');
      }
    }

    setRegistryInlineCss(cfg.custom_css || '');
  }

  function anchorHostSection(el) {
    if (!el || !el.closest) return null;
    return el.closest('.shopify-section');
  }

  function invisiSwingHudGateOn() {
    return document.documentElement.hasAttribute('data-odx-nav-invisi-swing');
  }

  function isHeroInvisiTargetSection(sectionEl) {
    if (!sectionEl || !sectionEl.querySelector) return false;
    return !!sectionEl.querySelector('[data-odx-home-invisi-target="true"]');
  }

  /** First `.anchor-point` in `sectionEl` that has a registry entry (document order). */
  function pickHudAnchorInSection(sectionEl) {
    if (!sectionEl || !sectionEl.querySelectorAll) return null;
    var points = sectionEl.querySelectorAll('.anchor-point');
    for (var i = 0; i < points.length; i++) {
      if (resolveHudForAnchor(points[i])) return points[i];
    }
    return null;
  }

  /**
   * Call **only** from invisi-swing at the incoming teleport (bar at target top − 100px, invisible).
   * Applies `hudRegistry` for that section’s anchor — not gated on EARLY_TRIGGER_PX.
   */
  function applyIncomingHudAtTeleport(sectionEl) {
    if (!sectionEl || !baselineHUD || !header) return;
    var ap = pickHudAnchorInSection(sectionEl);
    if (!ap) {
      if (appliedHudKey !== null) revertToBaseline();
      return;
    }
    if (ap.dataset && ap.dataset.hudEnabled === 'false') {
      if (appliedHudKey !== null) revertToBaseline();
      return;
    }
    var resolved = resolveHudForAnchor(ap);
    if (!resolved) {
      if (appliedHudKey !== null) revertToBaseline();
      return;
    }
    if (appliedHudKey === resolved.key) return;
    applyHudFromConfig(resolved.config);
    appliedHudKey = resolved.key;
  }

  /** Last `.anchor-point` in document order whose top edge is at or above the 50px buffer (earlier HUD swap). */
  function activeAnchorPoint() {
    var points = document.querySelectorAll('.anchor-point');
    var last = null;
    for (var i = 0; i < points.length; i++) {
      var r = points[i].getBoundingClientRect();
      if (r.top <= EARLY_TRIGGER_PX) last = points[i];
    }
    return last;
  }

  /**
   * Single source of truth for HUD vs baseline. Revert as soon as the active anchor
   * is HUD-off (DOM or registry). Class/blur attrs use CSS 0.25s; inline baselines restore after HUD class drops.
   */
  function runHudTick() {
    if (!header || !baselineHUD) return;

    var active = null;
    var invisi = invisiSwingHudGateOn();
    var tid = invisi && window.odxInvisiSwingHud ? window.odxInvisiSwingHud.teleportSectionId : null;

    if (tid) {
      var secEl = document.getElementById(tid);
      active = secEl ? pickHudAnchorInSection(secEl) : null;
    } else {
      active = activeAnchorPoint();
      if (invisi && active) {
        var host = anchorHostSection(active);
        if (host && !isHeroInvisiTargetSection(host)) active = null;
      }
    }

    if (!active) {
      if (appliedHudKey !== null) revertToBaseline();
      return;
    }

    if (active.dataset && active.dataset.hudEnabled === 'false') {
      if (appliedHudKey !== null) revertToBaseline();
      return;
    }

    var resolved = resolveHudForAnchor(active);
    if (!resolved) {
      if (appliedHudKey !== null) revertToBaseline();
      return;
    }

    var rkey = resolved.key;
    if (rkey && appliedHudKey === rkey) return;
    applyHudFromConfig(resolved.config);
    appliedHudKey = rkey;
  }

  function tickFromScroll() {
    rafScheduled = false;
    runHudTick();
  }

  function schedule() {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(tickFromScroll);
  }

  function bindObservers() {
    var points = document.querySelectorAll('.anchor-point');
    if (io) {
      io.disconnect();
    }
    io = new IntersectionObserver(
      function () {
        runHudTick();
      },
      { root: null, rootMargin: '0px', threshold: [0, 0.01, 0.05, 0.1, 0.25, 0.5, 0.75, 1] },
    );
    points.forEach(function (p) {
      io.observe(p);
    });
  }

  function init() {
    collectBaseline();
    bindObservers();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('hashchange', schedule);
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.odxHeaderHud = {
    refreshBaseline: collectBaseline,
    schedule: schedule,
    getRegistry: getRegistry,
    applyIncomingHudAtTeleport: applyIncomingHudAtTeleport,
  };
})();
